// Libs
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';

// Assets
import { ReactComponent as RemoveIcon } from '../../../assets/remove.svg';
import { ReactComponent as DragIcon } from '../../../assets/drag.svg';

// Components
import SearchInput from '../../../components/search-input';

// Interfaces
import { Stop as StopI } from '../../../interfaces';

// Constants
import { MAPBOX_TOKEN } from '../../../constants/tokens';

// Services
import GeocodingService from '@mapbox/mapbox-sdk/services/geocoding';

// Hooks
import useDebouncedQuery from '../../../hooks/useDebouncedQuery';

import './stop.scss';

const geocodingService = GeocodingService({
	accessToken: MAPBOX_TOKEN
});

interface StopProps {
	color: {
		previous?: string | null;
		current: string | null;
		next?: string | null;
	};
	stop: Partial<StopI>;
	draggable?: boolean;
	removable?: boolean;
	autoFocusInput?: boolean;
	placeholder?: string;
	onRemove?: () => void;
	onChange?: (stop: StopI) => void;
	onDragEnd?: (position: any) => void;
}

const Stop: React.FunctionComponent<StopProps> = ({
	color,
	stop,
	draggable = true,
	removable = true,
	autoFocusInput = false,
	placeholder,
	onRemove,
	onChange,
	onDragEnd
}) => {
	const [zIndex, setZIndex] = useState(1);
	const [searchValue, setSearchValue] = useState<string>('');
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const { data } = useDebouncedQuery(
		['location', searchValue],
		() =>
			geocodingService
				.forwardGeocode({ mode: 'mapbox.places', query: searchValue, autocomplete: true })
				.send()
				.then(data => data.body),
		{ enabled: searchValue.length > 2, delay: 500 }
	);
	const stopRef = useRef<HTMLDivElement | null>(null);
	const searchInputRef = useRef<HTMLInputElement | null>(null);
	const isInDragMode = useRef(false);

	const mouseDownY = useRef(0);

	function onMouseDown(e: React.MouseEvent<SVGSVGElement, MouseEvent>) {
		mouseDownY.current = e.clientY;
		isInDragMode.current = true;
		setZIndex(10);
	}

	function onMouseMove(e: MouseEvent) {
		if (isInDragMode.current) {
			const top = e.clientY - mouseDownY.current;

			if (stopRef.current) {
				stopRef.current.style.top = top + 'px';
			}
		}
	}

	function onMouseUp(e: MouseEvent) {
		if (isInDragMode.current) {
			const top = e.clientY - mouseDownY.current;

			isInDragMode.current = false;
			mouseDownY.current = 0;

			onDragEnd?.(top);
			setZIndex(1);
		}

		if (stopRef.current) {
			stopRef.current.style.top = '0px';
		}
	}

	function onSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
		setSearchValue(e.target.value);
	}

	function onSearchOptionSelect(value: StopI) {
		onChange?.(value);
	}

	function onSearchFocus() {
		setIsSearching(true);
	}

	function onSearchBlur() {
		setIsSearching(false);
	}

	useLayoutEffect(() => {
		if (autoFocusInput) {
			searchInputRef.current?.focus();
		}
	}, []);

	useEffect(() => {
		window.addEventListener('mouseup', onMouseUp);
		window.addEventListener('mousemove', onMouseMove);

		return () => {
			window.removeEventListener('mouseup', onMouseUp);
		};
	}, []);

	return (
		<div className="stop" style={{ zIndex }}>
			<div className="stop__wrapper" ref={stopRef}>
				<div className="stop__point" style={{ background: color.current ?? '' }}>
					{removable && (
						<RemoveIcon
							className={clsx('stop__point__remove', { dragging: isInDragMode.current })}
							onClick={onRemove}
						/>
					)}
				</div>
				{!isSearching && (
					<>
						<p className="stop__label">
							{stop.label?.substr(0, stop.label.lastIndexOf(','))}
							<span className="stop__label__secondary">
								{stop.label?.substr(stop.label.lastIndexOf(','))}
							</span>
						</p>
						{draggable && <DragIcon className="stop__drag" onMouseDown={onMouseDown} />}
					</>
				)}
			</div>

			<div style={{ marginLeft: '20px', width: '100%', padding: '9px 16px', marginRight: '18px' }}>
				<SearchInput
					ref={searchInputRef}
					value={isSearching ? searchValue : ''}
					placeholder={placeholder}
					options={data?.features?.map((feature: any) => ({
						label: feature.place_name,
						value: {
							label: feature.place_name,
							coordinates: feature.geometry.coordinates
						}
					}))}
					style={{
						padding: '0 6px',
						borderColor: isSearching ? color.current! : stop.label ? 'transparent' : '#e9e9e9'
					}}
					onSelect={onSearchOptionSelect}
					onFocus={onSearchFocus}
					onBlur={onSearchBlur}
					onChange={onSearchChange}
				/>
			</div>
		</div>
	);
};

export default Stop;
